import { callAdminApi } from "./callAdminApi";

import { 
  CmsLoginParams, 
  AdminListParams, 
  CreateAdminData, 
  UpdateAdminData, 
  UserListParams, 
  AuctioneerListParams, 
  CreateAuctioneerData,
  UpdateAuctioneerData,
  AuctionListParams, 
  LotListParams,
  CreateLotData,
  UpdateLotData,
  CreateTermsParams,
  UpdateLotSequenceParams,
  PushRecordListParams,
  CreatePushData,
  LiveBiddingMonitorParams,
  PaymentListParams,
  UpdatePaymentParams
} from "./adminApi.types";

// 🔐 인증 (Auth)

// 🔐 인증 (Auth) / 관리자 로그인
// 환경에 따른 API 호스트 결정
const getApiHost = () => {
  // 브라우저 환경에서 프로토콜 확인
  if (typeof window !== 'undefined') {
    const isHttps = window.location.protocol === 'https:';
    if (!isHttps) {
      // HTTP 환경 (테스트 서버)
      return process.env.REACT_APP_API_DEV_URL;
    } else {
      // HTTPS 환경 (운영 서버)
      return process.env.REACT_APP_API_PROD_URL;
    }
  }
  
  // 서버사이드에서는 기본값 사용
  return process.env.REACT_APP_API_HOST;
};

const API_HOST = getApiHost();

// 로그인 (토큰 불필요)
export async function cmsLogin(params: CmsLoginParams) {
  return callAdminApi({
    title: "관리자 로그인",
    url: `${API_HOST}/api/cms/login`,
    method: "POST",
    body: params,
    isCallPageLoader: true,
    withToken: false, // 로그인 시에는 토큰 불필요
  });
}

// 로그아웃 (토큰 필요)
export async function cmsLogout() {
  return callAdminApi({
    title: "관리자 로그아웃", 
    url: `${API_HOST}/api/cms/logout`,
    method: "DELETE",
    isCallPageLoader: true,
    withToken: true, // 로그아웃 시에는 토큰 필요
  });
}

// 🔧 관리자 관리 (Admin Management)

// 관리자 리스트 조회
export async function getAdminList(params: AdminListParams) {
  return callAdminApi({
    title: "관리자 리스트 조회",
    url: `${API_HOST}/api/cms/admins`,
    method: "GET",
    params: {
      keyword: params.keyword || '',
      fromDate: params.fromDate || '2025-01-01',
      toDate: params.toDate || '2025-12-31',
    },
    isCallPageLoader: true,
    withToken: true, // 관리자 리스트 조회는 토큰 필요
  });
}

// 특정 관리자 정보 조회
export async function getAdminDetail(adminId: string) {
  return callAdminApi({
    title: "관리자 상세 정보 조회",
    url: `${API_HOST}/api/cms/admins/${adminId}`,
    method: "GET",
    isCallPageLoader: true,
  });
}

// 관리자 ID 중복 체크
export async function checkAdminId(adminId: string) {
  return callAdminApi({
    title: "관리자 ID 중복 체크",
    url: `${API_HOST}/api/cms/admins/check-admin-id`,
    method: "POST",
    body: { adminId },
    isCallPageLoader: true,
    withToken: true,
  });
}

// 관리자 생성
export async function createAdmin(adminData: CreateAdminData) {
  return callAdminApi({
    title: "관리자 생성",
    url: `${API_HOST}/api/cms/admins`,
    method: "POST",
    body: adminData,
    isCallPageLoader: true,
  });
}

// 관리자 정보 수정
export async function updateAdmin(adminId: string, adminData: UpdateAdminData) {
  return callAdminApi({
    title: "관리자 정보 수정",
    url: `${API_HOST}/api/cms/admins/${adminId}`,
    method: "PATCH",
    body: adminData,
    isCallPageLoader: true,
  });
}

// 관리자 삭제
export async function deleteAdmin(adminId: string) {
  return callAdminApi({
    title: "관리자 삭제",
    url: `${API_HOST}/api/cms/admins/${adminId}`,
    method: "DELETE",
    isCallPageLoader: true,
  });
}

// 🔧 유저 관리 (User Management)

// 유저 리스트 조회
export async function getUserList(params: UserListParams) {
  return callAdminApi({
    title: "유저 리스트 조회",
    url: `${API_HOST}/api/cms/users`,
    method: "GET",
    params: {
      keyword: params.keyword || '',
      fromDate: params.fromDate || '2025-01-01',
      toDate: params.toDate || '2025-12-31',
    },
    isCallPageLoader: true,
    withToken: true, // 유저 리스트 조회는 토큰 필요
  });
}

// 🏛️ 경매사 관리 (Auctioneer Management)

// 경매사 리스트 조회
export async function getAuctioneerList(params: AuctioneerListParams) {
  return callAdminApi({
    title: "경매사 리스트 조회",
    url: `${API_HOST}/api/cms/auctioneers`,
    method: "GET",
    params: {
      keyword: params.keyword || '',
      fromDate: params.fromDate || '2025-01-01',
      toDate: params.toDate || '2025-12-31',
    },
    isCallPageLoader: true,
    withToken: true,
  });
}

// 경매사 간단 조회 (검색용)
export async function getAuctioneerListCompact(name?: string) {
  return callAdminApi({
    title: "경매사 간단 조회",
    url: `${API_HOST}/api/cms/auctioneers/compact`,
    method: "GET",
    params: name ? { name } : {},
    isCallPageLoader: true,
    withToken: true,
  });
}

// 경매사 생성
export async function createAuctioneer(auctioneerData: CreateAuctioneerData) {
  return callAdminApi({
    title: "경매사 생성",
    url: `${API_HOST}/api/cms/auctioneers`,
    method: "POST",
    body: auctioneerData,
    isCallPageLoader: true,
    withToken: true,
  });
}

// 경매사 정보 수정
export async function updateAuctioneer(index: number, auctioneerData: UpdateAuctioneerData) {
  return callAdminApi({
    title: "경매사 정보 수정",
    url: `${API_HOST}/api/cms/auctioneers/${index}`,
    method: "PATCH",
    body: auctioneerData,
    isCallPageLoader: true,
    withToken: true,
  });
}

// 경매사 삭제
export async function deleteAuctioneer(index: number) {
  return callAdminApi({
    title: "경매사 삭제",
    url: `${API_HOST}/api/cms/auctioneers/${index}`,
    method: "DELETE",
    isCallPageLoader: true,
    withToken: true,
  });
}

// 📊 경매 관리 (Auction Management)

// 경매 간단 조회 (검색용)
export async function getAuctionListCompact(keyword?: string) {
  return callAdminApi({
    title: "경매 간단 조회",
    url: `${API_HOST}/api/cms/auctions/compact`,
    method: "GET",
    params: keyword ? { keyword } : {},
    isCallPageLoader: true,
    withToken: true,
  });
}

// 순서 관리용 경매 조회 (경매사별)
export async function getAuctionListForOrder(auctioneerIndex: number, auctionIndex?: number) {
  const params: { auctioneerIndex: number; auctionIndex?: number } = { auctioneerIndex };
  
  // auctionIndex가 있으면 추가 (사전입찰이 시작된 경우)
  if (auctionIndex !== undefined) {
    params.auctionIndex = auctionIndex;
  }
  
  return callAdminApi({
    title: "순서 관리용 경매 조회",
    url: `${API_HOST}/api/cms/auctions/compact`,
    method: "GET",
    params,
    isCallPageLoader: true,
    withToken: true,
  });
}

// 경매 생성
export async function createAuction(auctionData: any) {
  return callAdminApi({
    title: "경매 생성",
    url: `${API_HOST}/api/cms/auctions`,
    method: "POST",
    body: auctionData,
    isCallPageLoader: true,
    withToken: true,
  });
}

// 경매 정보 수정
export async function updateAuction(index: number, auctionData: any) {
  return callAdminApi({
    title: "경매 정보 수정",
    url: `${API_HOST}/api/cms/auctions/${index}`,
    method: "PATCH",
    body: auctionData,
    isCallPageLoader: true,
    withToken: true,
  });
}

// 경매 삭제
export async function deleteAuction(index: number) {
  return callAdminApi({
    title: "경매 삭제",
    url: `${API_HOST}/api/cms/auctions/${index}`,
    method: "DELETE",
    isCallPageLoader: true,
    withToken: true,
  });
}

// 경매 리스트 조회
export async function getAuctionList(params: AuctionListParams) {
  return callAdminApi({
    title: "경매 리스트 조회",
    url: `${API_HOST}/api/cms/auctions`,
    method: "GET",
    params: {
      keyword: params.keyword || '',
      fromDate: params.fromDate || '2025-01-01',
      toDate: params.toDate || '2025-12-31',
    },
    isCallPageLoader: true,
    withToken: true, // 경매 리스트 조회는 토큰 필요
  });
}

// 경매 조회
export async function getAuctionDetail(index: number) {
  return callAdminApi({
    title: "경매 조회",
    url: `${API_HOST}/api/cms/auctions/${index}`,
    method: "GET",
    isCallPageLoader: true,
    withToken: true,
  });
}

// LOT 리스트 조회
export async function getLotList(params: LotListParams) {
  return callAdminApi({
    title: "LOT 리스트 조회",
    url: `${API_HOST}/api/cms/lots`,
    method: "GET",
    params: {
      keyword: params.keyword || '',
      fromDate: params.fromDate || '2025-01-01',
      toDate: params.toDate || '2025-12-31',
    },
    isCallPageLoader: true,
    withToken: true, // LOT 리스트 조회는 토큰 필요
  });
}

// 카테고리 리스트 조회
export async function getCategoriesList() {
  return callAdminApi({
    title: "카테고리 리스트 조회",
    url: `${API_HOST}/api/cms/lots/categories`,
    method: "GET",
    isCallPageLoader: true,
    withToken: true,
  });
}

// 특정 LOT 정보 조회
export async function getLotDetail(lotIndex: number) {
  return callAdminApi({
    title: "LOT 상세 정보 조회",
    url: `${API_HOST}/api/cms/lots/${lotIndex}`,
    method: "GET",
    isCallPageLoader: true,
    withToken: true,
  });
}

// 특정 LOT의 사전입찰 조회
export async function getLotPreBids(lotIndex: number) {
  return callAdminApi({
    title: "LOT 사전입찰 조회",
    url: `${API_HOST}/api/cms/lots/${lotIndex}/pre-bid`,
    method: "GET",
    isCallPageLoader: true,
    withToken: true,
  });
}

// LOT 생성
export async function createLot(lotData: CreateLotData) {
  return callAdminApi({
    title: "LOT 생성",
    url: `${API_HOST}/api/cms/lots`,
    method: "POST",
    body: lotData,
    isCallPageLoader: true,
    withToken: true,
  });
}

// LOT 정보 수정
export async function updateLot(lotIndex: number, lotData: UpdateLotData) {
  return callAdminApi({
    title: "LOT 정보 수정",
    url: `${API_HOST}/api/cms/lots/${lotIndex}`,
    method: "PATCH",
    body: lotData,
    isCallPageLoader: true,
    withToken: true,
  });
}

// LOT 삭제
export async function deleteLot(lotIndex: number) {
  return callAdminApi({
    title: "LOT 삭제",
    url: `${API_HOST}/api/cms/lots/${lotIndex}`,
    method: "DELETE",
    isCallPageLoader: true,
    withToken: true,
  });
}

// 📄 약관 관리 (Terms Management)

// 약관 조회
export async function getTermsList() {
  return callAdminApi({
    title: "약관 조회",
    url: `${API_HOST}/api/cms/terms`,
    method: "GET",
    isCallPageLoader: true,
    withToken: true, // 약관 조회는 토큰 필요
  });
}

// 약관 생성
export async function createTerms(index: number, params: CreateTermsParams) {
  return callAdminApi({
    title: "약관 생성",
    url: `${API_HOST}/api/cms/terms?index=${index}`,
    method: "PUT",
    body: params,
    isCallPageLoader: true,
    withToken: true, // 약관 생성은 토큰 필요
  });
}

// Lot 순서 업데이트
export async function updateLotSequence(params: UpdateLotSequenceParams) {
  return callAdminApi({
    title: "Lot 순서 업데이트",
    url: `${API_HOST}/api/cms/lots/sequence`,
    method: "PATCH",
    body: params,
    isCallPageLoader: true,
    withToken: true,
  });
}

// 작품 좋아요/취소 (Like/Unlike Lot)
export async function likeLot(lotId: number) {
  return callAdminApi({
    title: "작품 좋아요/취소",
    url: `${API_HOST}/api/users/lots/${lotId}/like`,
    method: "POST",
    isCallPageLoader: true,
    withToken: true,
  });
}

// LOT 추천여부 업데이트
export async function updateLotRecommendStatus(lotIndex: number, isRecommended: boolean, recommendNo?: number | null) {
  // 기존 updateLot API를 사용하여 recommendNo만 업데이트
  return callAdminApi({
    title: "LOT 추천여부 업데이트",
    url: `${API_HOST}/api/cms/lots/${lotIndex}`,
    method: "PATCH",
    body: { 
      recommendNo: isRecommended ? recommendNo : null
    },
    isCallPageLoader: false, // 토글 액션이므로 로더 비활성화
    withToken: true,
  });
}

// 📢 푸시 관리 (Push Management)

// 푸시 기록 조회
export async function getPushRecordList(params: PushRecordListParams) {
  return callAdminApi({
    title: "푸시 기록 조회",
    url: `${API_HOST}/api/cms/push`,
    method: "GET",
    params: {
      fromDate: params.fromDate || '2025-01-01',
      toDate: params.toDate || '2025-12-31',
    },
    isCallPageLoader: true,
    withToken: true,
  });
}

// 푸시 생성
export async function createPush(pushData: CreatePushData) {
  return callAdminApi({
    title: "푸시 생성",
    url: `${API_HOST}/api/cms/push`,
    method: "POST",
    body: pushData,
    isCallPageLoader: true,
    withToken: true,
  });
}

// 📊 실시간 입찰 모니터링 (Live Bidding Monitoring)

// 실시간 입찰 모니터링 조회
export async function getLiveBiddingMonitor(params: LiveBiddingMonitorParams) {
  return callAdminApi({
    title: "실시간 입찰 모니터링 조회",
    url: `${API_HOST}/api/cms/lots/live`,
    method: "GET",
    params: {
      keyword: params.keyword || '',
      fromDate: params.fromDate || '2025-01-01',
      toDate: params.toDate || '2025-12-31',
    },
    isCallPageLoader: true,
    withToken: true,
  });
}

// 💳 결제 관리 (Payment Management)

// 결제 조회
export async function getPaymentList(params: PaymentListParams) {
  return callAdminApi({
    title: "결제 조회",
    url: `${API_HOST}/api/cms/payment`,
    method: "GET",
    params: {
      keyword: params.keyword || '',
      fromDate: params.fromDate || '2025-01-01',
      toDate: params.toDate || '2026-12-31',
    },
    isCallPageLoader: true,
    withToken: true,
  });
}

// 결제 이력 수정
export async function updatePayment(paymentIndex: number, params: UpdatePaymentParams) {
  return callAdminApi({
    title: "결제 이력 수정",
    url: `${API_HOST}/api/cms/payment/${paymentIndex}`,
    method: "PATCH",
    body: {
      ...(params.status && { status: params.status }),
      ...(params.isRefund !== undefined && { isRefund: params.isRefund }),
      ...(params.refundAmount !== undefined && { refundAmount: params.refundAmount }),
      ...(params.memo !== undefined && { memo: params.memo }),
    },
    isCallPageLoader: true,
    withToken: true,
  });
}
